import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  TablePagination,
  Chip,
  Stack,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axiosInstance from '../utils/axiosConfig'; 

interface Employee {
  _id: string;
  name: string;
}

interface Expense {
  _id: string;
  date: string;
  staffId: string;
  amount: number;
  status: string;
  description: string;
  location?: string;
}

const ExpenseListPage: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [staffMembers, setStaffMembers] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedStaffId, setSelectedStaffId] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Bulk selection
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const expensesResponse = await axiosInstance.get<Expense[]>('/api/expenses');
        setExpenses(expensesResponse.data);
  
        const staffResponse = await axiosInstance.get<Employee[]>('/api/personnel');
        setStaffMembers(staffResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, []);

  // Staff name lookup
  const staffMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    staffMembers.forEach(member => {
      map[member._id] = member.name;
    });
    return map;
  }, [staffMembers]);

  const getEmployeeName = (staffId: string): string => {
    if (!staffId) return 'Unknown';
    return staffMap[staffId] || 'Unknown Employee';
  };


  // Filter and sort expenses
  const filteredAndSortedExpenses = useMemo(() => {
    let filtered = expenses;

    // Search filter (searches description, location, category, staff name, amount)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(expense => {
        const staffName = getEmployeeName(expense.staffId).toLowerCase();
        const description = (expense.description || '').toLowerCase();
        const amount = expense.amount.toString();
        
        return (
          description.includes(searchLower) ||
          staffName.includes(searchLower) ||
          amount.includes(searchLower)
        );
      });
    }

    // Staff filter
    if (selectedStaffId !== 'all') {
      filtered = filtered.filter(expense => expense.staffId === selectedStaffId);
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(expense => 
        expense.status.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // Date range filter
    if (startDate) {
      filtered = filtered.filter(expense => 
        new Date(expense.date) >= startDate
      );
    }
    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(expense => 
        new Date(expense.date) <= endOfDay
      );
    }

    // Sort by date - newest first
    return filtered.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [expenses, searchTerm, selectedStaffId, selectedStatus, startDate, endDate, staffMap]);

  // Calculate totals
  const totals = useMemo(() => {
    const total = filteredAndSortedExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    const uploaded = filteredAndSortedExpenses
      .filter(e => e.status.toLowerCase() === 'uploaded')
      .reduce((sum, exp) => sum + exp.amount, 0);
    const approved = filteredAndSortedExpenses
      .filter(e => e.status.toLowerCase() === 'approved')
      .reduce((sum, exp) => sum + exp.amount, 0);

    return { total, uploaded, approved };
  }, [filteredAndSortedExpenses]);

  // Paginated expenses
  const paginatedExpenses = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedExpenses.slice(start, start + rowsPerPage);
  }, [filteredAndSortedExpenses, page, rowsPerPage]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const handleDelete = async (expenseId: string) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await axiosInstance.delete(`/api/expenses/${expenseId}`);
        setExpenses(expenses.filter(expense => expense._id !== expenseId));
      } catch (err) {
        console.error('Error deleting expense:', err);
        setError('Failed to delete expense. Please try again later.');
      }
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = paginatedExpenses.map(expense => expense._id);
      setSelected(allIds);
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (expenseId: string) => {
    setSelected(prev => {
      if (prev.includes(expenseId)) {
        return prev.filter(id => id !== expenseId);
      } else {
        return [...prev, expenseId];
      }
    });
  };

  const handleBulkDelete = async () => {
    try {
      // Use the bulk delete endpoint instead of multiple single deletes
      const response = await axiosInstance.post('/api/expenses/bulk-delete', {
        expenseIds: selected
      });
      
      console.log('Bulk delete response:', response.data);
      
      // Update local state
      setExpenses(expenses.filter(expense => !selected.includes(expense._id)));
      setSelected([]);
      setBulkDeleteDialogOpen(false);
      
      // Show success message
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error deleting expenses:', err);
      setError('Failed to delete expenses. Please try again.');
      setBulkDeleteDialogOpen(false);
    }
  };

  const isSelected = (id: string) => selected.includes(id);
  const numSelected = selected.length;
  const numOnPage = paginatedExpenses.length;

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStaffId('all');
    setSelectedStatus('all');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Quick date filters
  const setThisMonth = () => {
    const now = new Date();
    setStartDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  };

  const setLastMonth = () => {
    const now = new Date();
    setStartDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    setEndDate(new Date(now.getFullYear(), now.getMonth(), 0));
  };

  const setThisYear = () => {
    const now = new Date();
    setStartDate(new Date(now.getFullYear(), 0, 1));
    setEndDate(new Date(now.getFullYear(), 11, 31));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6">Loading expenses...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h4">Expense Management</Typography>
            {numSelected > 0 && (
              <Chip 
                label={`${numSelected} selected`} 
                color="primary" 
                onDelete={() => setSelected([])}
              />
            )}
          </Box>
          <Box display="flex" gap={2}>
            {numSelected > 0 && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={() => setBulkDeleteDialogOpen(true)}
              >
                Delete {numSelected} {numSelected === 1 ? 'Expense' : 'Expenses'}
              </Button>
            )}
            <Button
              component={Link}
              to="/admin/expenses/new"          
              variant="contained"
              color="primary"
              size="large"
            >
              Add New Expense
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">Total (Filtered)</Typography>
            <Typography variant="h5">{formatCurrency(totals.total)}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">Uploaded</Typography>
            <Typography variant="h5" color="warning.main">{formatCurrency(totals.uploaded)}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">Approved</Typography>
            <Typography variant="h5" color="success.main">{formatCurrency(totals.approved)}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">Showing</Typography>
            <Typography variant="h5">{filteredAndSortedExpenses.length} of {expenses.length}</Typography>
          </Paper>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            {/* Search Box */}
            <TextField
              fullWidth
              placeholder="Search expenses (description, location, staff name, category, amount...)"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Filter Row 1 */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Staff Member</InputLabel>
                <Select
                  value={selectedStaffId}
                  label="Staff Member"
                  onChange={(e) => {
                    setSelectedStaffId(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All Staff</MenuItem>
                  {staffMembers.map(staff => (
                    <MenuItem key={staff._id} value={staff._id}>
                      {staff.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  label="Status"
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="uploaded">Uploaded</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Date Filters Row */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <DatePicker
                label="From Date"
                value={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  setPage(0);
                }}
                slotProps={{ 
                  textField: { 
                    size: 'small',
                    sx: { minWidth: 150 }
                  },
                  field: { clearable: true }
                }}
              />
              <DatePicker
                label="To Date"
                value={endDate}
                onChange={(date) => {
                  setEndDate(date);
                  setPage(0);
                }}
                slotProps={{ 
                  textField: { 
                    size: 'small',
                    sx: { minWidth: 150 }
                  },
                  field: { clearable: true }
                }}
              />

              {/* Quick Date Buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label="This Month" onClick={setThisMonth} clickable />
                <Chip label="Last Month" onClick={setLastMonth} clickable />
                <Chip label="This Year" onClick={setThisYear} clickable />
              </Box>

              <Button 
                variant="outlined" 
                onClick={clearFilters}
                startIcon={<ClearIcon />}
                sx={{ ml: 'auto' }}
              >
                Clear All Filters
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* Table */}
        {filteredAndSortedExpenses.length === 0 ? (
          <Alert severity="info">
            {expenses.length === 0 
              ? 'No expenses found. Add your first expense to get started.'
              : 'No expenses match your current filters. Try adjusting your search criteria.'
            }
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={numSelected > 0 && numSelected < numOnPage}
                        checked={numOnPage > 0 && numSelected === numOnPage}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Staff Member</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedExpenses.map((expense) => {
                    const isItemSelected = isSelected(expense._id);
                    return (
                      <TableRow 
                        key={expense._id}
                        hover
                        onClick={() => handleSelectOne(expense._id)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        selected={isItemSelected}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' } 
                        }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleSelectOne(expense._id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {new Date(expense.date).toLocaleDateString('en-IE', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getEmployeeName(expense.staffId)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {expense.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatCurrency(expense.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={expense.status.charAt(0).toUpperCase() + expense.status.slice(1)}
                            size="small"
                            color={
                              expense.status.toLowerCase() === 'approved'
                                ? 'success'
                                : expense.status.toLowerCase() === 'uploaded'
                                ? 'warning'
                                : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Box display="flex" justifyContent="center" gap={0.5}>
                            <Tooltip title="View Details">
                              <IconButton
                                component={Link}
                                to={`/admin/expenses/${expense._id}`}
                                size="small"
                                color="primary"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                component={Link}
                                to={`/admin/expenses/${expense._id}/edit`}
                                size="small"
                                color="secondary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => handleDelete(expense._id)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredAndSortedExpenses.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ mt: 2 }}
            />
          </>
        )}

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog
          open={bulkDeleteDialogOpen}
          onClose={() => setBulkDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Bulk Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete {numSelected} {numSelected === 1 ? 'expense' : 'expenses'}? 
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkDelete} color="error" variant="contained">
              Delete {numSelected} {numSelected === 1 ? 'Expense' : 'Expenses'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ExpenseListPage;